import React, {createRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {uid} from 'react-uid'
import classNames from 'classnames'
import * as ReactDOM from 'react-dom'

import '../../Message.css'
import './TemplateMessage.css'
import Button from '../Button'
import {TemplateImg} from './TemplateImg'
import {NextIcon, PrevIcon} from '../../../../icons/Icons'
import {generatePostBackObject, zoomImage} from '../../../../actions/static'
import {messageWidth, getImageHeight, carouselContainerWidth, messageLogoWidth} from './config'
import MessageLogo from '../../MessageLogo'
import QuickReplyList from '../../Reply/QuickReplyList'
import classes from './styles.module.scss'

const TemplateMessage = props => {
  const {
    postMessage,
    message,
    showLogo,
    device,
    settings,
    setIframe,
    showReply,
    isFullScreenWidget,
    isMobile,
    scrollToBottom,
    isMaximized,
    isPanoramaPost,
    avatarLogo,
    setIsImageZoomed,
    isImageLoaded,
    setIsImageLoaded
  } = props

  const { elements } = message

  const [showScroll, setShowScroll] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [leftScrollValue, setLeftScrollValue] = useState(0)

  const carousel = useRef(null)

  const refs = useMemo(() => {
    if (elements.length) {
      return elements.map((reply, index) => `reply${index}`)
    } else {
      return []
    }
  }, [elements])

  const refsArray = useMemo(() => refs.map(() => createRef()), [refs])

  useEffect(() => {
    if (elements) {
      const showScroll = scrollCounter()
      setShowScroll(showScroll)
    }
  }, [])

  useEffect(() => {
    setCurrentIndex(0)
    setLeftScrollValue(0)
    setShowScroll(scrollCounter())
    carousel.current.scrollLeft = 0
  }, [device])

  const nextItem = () => scrollItems(true)
  const prevItem = () => scrollItems(false)

  const scrollItems = next => {
    const isLastReply = currentIndex === refsArray.length - 1
    const index = isLastReply ? refsArray.length - 2 : currentIndex
    const scrollValue = ReactDOM.findDOMNode(refsArray[index].current).getBoundingClientRect().width
    let countedScrollValue = leftScrollValue + (next ? scrollValue : -scrollValue)
    const countedCurrentIndex = currentIndex + (next ? 1 : -1)

    if (!next) {
      countedScrollValue -= 0
    }

    setCurrentIndex(countedCurrentIndex)
    setLeftScrollValue(countedScrollValue)
    carousel.current.scrollLeft = countedScrollValue
  }

  const scrollCounter = () => {
    let sumWidth = 0

    elements.forEach((slide, index) => {
      sumWidth += ReactDOM.findDOMNode(refsArray[index].current).getBoundingClientRect().width
    })
    sumWidth += 0

    return carousel.current.offsetWidth < sumWidth
  }

  const checkIfScrollShouldBeShowed = () => {
    const carouselNode = carousel.current
    return carouselNode && carouselNode.scrollWidth - carouselNode.offsetWidth > leftScrollValue
  }

  const checkIfNeedRightArrow = slides => {
    return (currentIndex < slides.length)
      && (checkIfScrollShouldBeShowed() || leftScrollValue === 0)
      && showScroll
  }

  const isLeftArrow = useMemo(() => {
    return carousel
      && currentIndex > 0
      && leftScrollValue > 0
  }, [currentIndex, carousel, leftScrollValue])

  const isRightArrowNeeded = useMemo(() => {
    return checkIfNeedRightArrow(elements)
  })

  const imgHeight = useMemo(() => getImageHeight(device), [device])

  const msgWidth = useCallback((deviceWidth) => messageWidth(deviceWidth), [device])

  const msgLogoWrap = useCallback((deviceWidth) => messageLogoWidth(deviceWidth), [device])

  const crsHolderWidth = useMemo(() => carouselContainerWidth(device.width), [device])

  const scrollContainerConditionClass = useMemo(() => {
    return classes.scrollContainer
  }, [classes])


  function handleImageClick(imageUrl) {
    setIsImageZoomed(true)
    return zoomImage(imageUrl);
  }

  return (
    <>
      <div
        style={showLogo ? msgLogoWrap(device.width) : msgWidth(device.width)}
        className={classNames(`${message.isFromBot}`, {
          'received-container_logo': showLogo,
          'received-container': !showLogo
        })}
      >
        {message.isFromBot && showLogo && <MessageLogo avatarLogo={avatarLogo} />}
        <div
          style={crsHolderWidth}
          className={classes.container}
        >
          {isLeftArrow &&
          <div
            onClick={prevItem}
            className={classNames('generic-list-prev-btn', classes.prevIcon)}
          >
            <PrevIcon/>
          </div>}
          <div
            ref={carousel}
            style={msgWidth(device.width)}
            className={scrollContainerConditionClass}
          >
            {elements &&
            elements.map((element, index) => {
              const imageUrl = element.image_url || element.imageUrl
              return (
                <div
                  style={msgWidth(device.width)}
                  className={classNames('generic_template')} key={uid(element)} ref={refsArray[index]}
                >
                  {element.default_action && element.default_action.url ? (
                    <a rel='noopener noreferrer' target='_blank' href={element.default_action.url}>
                      <p>{element.default_action.url}</p>
                      <TemplateImg
                        url={imageUrl}
                        classes={classes.templateImg}
                        styles={imgHeight}
                      />
                    </a>) :
                    (
                      <TemplateImg
                        url={imageUrl}
                        classes={classes.templateImg}
                        imageHeight={imgHeight}
                        onclick={() => handleImageClick(imageUrl)}
                      />
                    )
                  }
                  {(element.title || element.subtitle) &&
                  <div className={classes.templateTopWrapper}>
                    {element.title &&
                    <p className={classes.templateTitle}>{element.title}</p>}
                    {element.subtitle &&
                    <p className={classes.templateSubtitle}>{element.subtitle}</p>}
                  </div>}
                  <div style={msgWidth(device.width)}>
                    {element.buttons &&
                    element.buttons.map(button => (
                      <Button
                        button={button}
                        key={uid(button)}
                        title={button.title}
                        setIframe={setIframe}
                        onClick={postMessage.bind(null, generatePostBackObject(button))}
                        settings={settings}
                        isPanoramaPost={isPanoramaPost}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          {isRightArrowNeeded &&
          <div
            className={classNames('generic-list-next-btn', classes.nextIcon, classes.nextIconPart)}
            onClick={nextItem}
          >
            <NextIcon/>
          </div>}
        </div>
      </div>
      {showReply && message && (
        <QuickReplyList
          isMobile={isMobile}
          message={message}
          replies={message.quickReplies || []}
          settings={settings}
          postMessage={postMessage}
          isFullScreenWidget={isFullScreenWidget}
          scrollToBottom={scrollToBottom}
          isMaximized={isMaximized}
          isImageLoaded={isImageLoaded}
          setIsImageLoaded={setIsImageLoaded}
        />
      )}
    </>
  )
}

export default TemplateMessage
